import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { BaseView, TranslatePipe } from 'O3_core';
import { IEmailQueue } from '../interfaces/email-queue.interface';
import { IEmailStatus } from '../interfaces/email-status.interface';
import { EmailQueueService } from '../services/email-queue.service';
import { EmailStatusService } from '../services/email-status.service';

@Component({
  selector: 'o3-notifications-email-queue-detail',
  imports: [CommonModule, TranslatePipe, RouterModule],
  templateUrl: './detail.html',
  styleUrl: './detail.css',
})
export class EmailQueueDetail extends BaseView implements OnInit {
  private readonly emailQueueService = inject(EmailQueueService);
  private readonly emailStatusService = inject(EmailStatusService);

  email = signal<IEmailQueue | null>(null);
  emailStatus = signal<IEmailStatus | null>(null);
  isLoading = signal<boolean>(true);

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.loadEmail(id);
    }
  }

  private async loadEmail(id: string): Promise<void> {
    this.isLoading.set(true);
    try {
      const [emailData, statusResult] = await Promise.all([
        firstValueFrom(this.emailQueueService.getById(id)),
        firstValueFrom(this.emailStatusService.list({})),
      ]);
      this.email.set(emailData);

      const status = (statusResult?.items ?? []).find(
        (s: IEmailStatus) => s.code === emailData.status,
      );
      this.emailStatus.set(status || null);
    } catch (error) {
      this.processError(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  goBack(): void {
    this.router.navigate(['/notifications/email-queue']);
  }

  formatJson(value: unknown): string {
    if (value == null) return '';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  formatSize(bytes: number): string {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
